<template>
  <div class="activity-history">
    <div
      v-if="isActivityGoalEnabled"
      class="activity-history-aggregated d-flex flex-wrap"
    >
      <div
        v-for="(item, index) in aggregatedInfo"
        :key="index"
        class="aggregated-item d-flex flex-grow-1 pa-5"
      >
        <div
          class="aggregated-item-icon-wrapper d-flex align-center justify-center flex-shrink-0"
        >
          <img :src="item.icon" :alt="item.icon" />
        </div>
        <div>
          <div class="aggregated-item-value">{{ item.value }}</div>
          <div class="aggregated-item-label">{{ item.label }}</div>
        </div>
      </div>
    </div>
    <div
      class="activity-history-list mt-6"
      :class="{ 'goal-disabled': !isActivityGoalEnabled }"
    >
      <div
        v-for="item in groupedBooks"
        :key="item[0].date"
        class="activity-history-date"
      >
        <div class="date-total d-flex justify-space-between my-3">
          <div>
            <span>{{ getFormattedDate(item[0].date) }}</span>
            <span v-if="isActivityGoalEnabled" class="ps-2">
              <span v-if="item[0].goal">
                {{
                  $t('ActivityHistory.day.header.goal', {
                    minutes: item[0].goal
                  })
                }}
              </span>
              <span v-else>{{ $t('ActivityHistory.day.header.noGoal') }}</span>
            </span>
          </div>
          <div class="d-flex align-center">
            <span>
              {{
                $t('ActivityHistory.day.header.total', {
                  hours: getReadTime(item[0].totalWords)
                })
              }}
            </span>
            <span v-if="isActivityGoalEnabled" class="ico-activity-goal">
              <img
                v-if="item[0].achievedGoal"
                :src="icoFireRed"
                :alt="icoFireRed"
              />
              <img v-else :src="icoFireGray" :alt="icoFireGray" />
            </span>
          </div>
        </div>
        <div
          v-for="activity in item[1]"
          :key="activity.changedAt"
          class="activity-history-book d-flex justify-space-between mt-2 pa-3"
          :class="{ disabled: isUnpublished(activity.book) }"
        >
          <BookItem
            :book="activity.book"
            :show-level="isActivityGoalEnabled"
            :lazy-load="imgLazyLoad"
          />
          <span class="book-reading-time ps-1"
            >{{ getReadTime(activity.readWords) }}h</span
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import dayJS from '@/dayJS';
import Utils from '@/services/utils/Utils';
import PublicationsTypesEnum from '@shared/enums/PublicationsTypesEnum';

import AppConstantsUtil from '@/services/utils/AppConstantsUtil';

import BookItem from '@/components/pages/UserActivity/BookItem.vue';
import images from '@/assets/images';

export default {
  name: 'ActivityHistoryList',
  components: {
    BookItem
  },
  props: {
    activities: {
      type: Array,
      required: true
    },
    imgLazyLoad: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      picBarChart: images.picBarChart,
      picFire: images.picFire,
      picLibrary: images.picLibrary,
      picSandClock: images.picSandClock,
      icoFireGray: images.icoFireGray,
      icoFireRed: images.icoFireRed
    };
  },
  computed: {
    ...mapGetters('LibraryStore', ['getPublicationById']),
    ...mapGetters('ContextStore', [
      'getSystemLanguage',
      'isActivityGoalEnabled'
    ]),
    goals() {
      return this.groupedBooks.reduce(
        (prev, curr) => (prev += curr[0].achievedGoal),
        0
      );
    },
    aggregatedInfo() {
      const booksIdsSet = new Set();
      let min;
      let max = 0;
      let totalWords = 0;

      this.activities.forEach(activity => {
        const difficulty = activity.book.difficulty;
        min = min && difficulty > min ? min : difficulty;
        max = max > difficulty ? max : difficulty;

        booksIdsSet.add(activity.book.id);
        totalWords += activity.readWords;
      });
      const minutes = totalWords / AppConstantsUtil.DEFAULT_AUDIO_SPEED;
      const duration = dayJS.getDuration(minutes * 60 * 1000);
      const info = [
        {
          icon: this.picBarChart,
          label: this.$t('ActivityHistory.levels.label'),
          value: min !== max ? `${min} - ${max}` : max
        },
        {
          icon: this.picFire,
          label: this.$t('ActivityHistory.goals.label'),
          value: this.goals
        },
        {
          icon: this.picLibrary,
          label: this.$t('ActivityHistory.readingBooks.label'),
          value: booksIdsSet.size
        },
        {
          icon: this.picSandClock,
          label: this.$t('ActivityHistory.readingTime.label'),
          value: this.$t('MyActivity.read.time', {
            hours: duration.hours(),
            minutes: duration.minutes()
          })
        }
      ];
      return info;
    },
    groupedBooks() {
      const grouped = [];
      this.activities.forEach(activity => {
        const index = grouped.findIndex(g => g[0]?.date === activity.date);
        if (index > -1) {
          grouped[index][1].push(activity);
        } else {
          grouped.push([
            {
              date: activity.date,
              goal: activity.goal,
              totalWords: activity.totalWords,
              achievedGoal: activity.achievedGoal
            },
            [activity]
          ]);
        }
      });
      return grouped;
    },
    weekdays() {
      return this.isActivityGoalEnabled
        ? dayJS.getWeekdaysShort(this.getSystemLanguage)
        : dayJS.getWeekdays(this.getSystemLanguage);
    }
  },
  methods: {
    isUnpublished(book) {
      return book?.type === PublicationsTypesEnum.UNPUBLISHED_DEFAULT_BOOK;
    },
    getReadTime(readWords) {
      return Utils.getFormattedMinutes(
        readWords / AppConstantsUtil.DEFAULT_AUDIO_SPEED
      );
    },
    getFormattedDate(date) {
      const dayIndex = dayJS.get(date).day();
      const dayName = this.weekdays.find(w => w.dayIndex === dayIndex).name;
      return this.$t('ActivityHistory.day.header.datetime', {
        date: Utils.dateFormat(date),
        weekday: dayName
      });
    }
  }
};
</script>

<style lang="less">
@import '~@/assets/styles/base.less';

.activity-history {
  .activity-history-aggregated {
    gap: 8px;
    .aggregated-item {
      gap: 12px;
      flex-basis: 0;
      align-items: center;
      border-radius: 12px;
      background-color: #f1f5f9;

      @media @narrow, @normal {
        min-width: calc(50% - 8px);
      }
      @media @narrow {
        flex-direction: column;
        align-items: start;
      }
      .aggregated-item-icon-wrapper {
        width: 44px;
        height: 44px;
        border-radius: 50%;
        background-color: var(--primary-color);

        img {
          width: 24px;
          height: 24px;
        }
      }
      .aggregated-item-value {
        color: var(--black-color);
        font-weight: 700;
        font-size: 18px;
      }
      .aggregated-item-label {
        color: #64748b;
      }
    }
  }
  .activity-history-date {
    .date-total {
      font-weight: 700;
      font-size: 12px;
      white-space: nowrap;

      svg {
        width: 16px;
        height: 16px;
        margin-inline-start: 8px;
        fill: var(--normal-border-color);
        &.active {
          fill: var(--secondary-accent-color);
        }
      }

      .ico-activity-goal {
        display: inline-flex;

        img {
          width: 16px;
          height: 16px;
          margin-inline-start: 8px;
        }
      }
    }
  }
  .activity-history-book {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    .book-reading-time {
      color: var(--secondary-accent-color);
      font-weight: 700;
      font-size: 12px;
    }

    &.disabled {
      opacity: 0.5;
      pointer-events: none;
    }
  }

  .activity-history-list.goal-disabled .book-reading-time {
    color: var(--black-color);
  }
}
</style>
